import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Settings, LogOut, Loader2 } from "lucide-react";
import { api, errMsg, toastErr } from "@/lib/api";
import { PersonaTab } from "@/components/admin/PersonaTab";
import { ModelTab } from "@/components/admin/ModelTab";
import { KnowledgeTab } from "@/components/admin/KnowledgeTab";
import { LogsTab } from "@/components/admin/LogsTab";
import { AnalyticsTab } from "@/components/admin/AnalyticsTab";
import { AdminsTab } from "@/components/admin/AdminsTab";

const TABS = [
  { id: "agent-persona", label: "Agent Persona", Component: PersonaTab },
  { id: "model", label: "Model", Component: ModelTab },
  { id: "knowledge-base", label: "Knowledge Base", Component: KnowledgeTab },
  { id: "chat-logs", label: "Chat Logs", Component: LogsTab },
  { id: "user-analytics", label: "User Analytics", Component: AnalyticsTab },
  { id: "admins", label: "Admins", Component: AdminsTab },
];

const Login = ({ error }) => {
  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/backoffice";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-8 w-full max-w-sm text-center shadow-sm">
        <div className="w-12 h-12 rounded-full bg-slate-900 text-white grid place-items-center mx-auto"><Settings size={20} /></div>
        <h1 className="mt-4 text-xl font-semibold text-slate-900">Backoffice</h1>
        <p className="mt-1 text-sm text-slate-500">Admins only. Sign in with your Google account.</p>
        {error && <p data-testid="login-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
        <button data-testid="backoffice-google-login-button" onClick={login} className="mt-6 w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl py-2.5 text-sm font-medium transition-colors">
          Continue with Google
        </button>
      </div>
    </div>
  );
};

export default function Backoffice() {
  const location = useLocation();
  const [user, setUser] = useState(undefined);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("agent-persona");
  const processed = useRef(false);

  useEffect(() => {
    const hash = location.hash;
    if (hash.includes("session_id=")) {
      if (processed.current) return;
      processed.current = true;
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      window.history.replaceState(null, "", "/backoffice");
      api.post("/auth/session", { session_id: sid })
        .then((r) => setUser(r.data))
        .catch((e) => { setError(errMsg(e)); setUser(null); });
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, [location.hash]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { toastErr(e); }
    setUser(null);
  };

  if (user === undefined) return <div className="min-h-screen grid place-items-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Login error={error} />;

  const Active = TABS.find((t) => t.id === tab).Component;
  return (
    <div data-testid="backoffice-dashboard" className="min-h-screen bg-slate-50 text-slate-900 p-4 sm:p-8 max-w-6xl mx-auto">
      <header className="mb-6 flex items-center justify-between gap-4 pb-5 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Settings size={20} className="text-slate-700" />
          <h1 className="text-lg sm:text-xl font-semibold tracking-tight">Agent Configuration</h1>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          {user.picture && <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />}
          <span data-testid="admin-email" className="hidden sm:inline">{user.email}</span>
          <button data-testid="logout-button" onClick={logout} className="p-2 rounded-lg hover:bg-slate-200/60" aria-label="Log out"><LogOut size={16} /></button>
        </div>
      </header>
      <nav className="flex gap-1.5 overflow-x-auto p-1.5 bg-slate-100/80 border border-slate-200 rounded-full mb-8 w-fit max-w-full">
        {TABS.map(({ id, label }) => (
          <button key={id} data-testid={`backoffice-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${tab === id ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"}`}>
            {label}
          </button>
        ))}
      </nav>
      <Active user={user} />
    </div>
  );
}
